import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Edit } from 'lucide-react';
import { authedJson } from '../../utils/adminApi';
import {
  GALLERY_STORAGE_BUCKET,
  uploadImageToGalleryMedia,
  uploadVideoToGalleryMedia
} from '../../utils/adminMedia';

export default function GalleryEditor() {
  const [gallery, setGallery] = useState([]);
  const [feedback, setFeedback] = useState({ error: '', success: '' });
  const [uploading, setUploading] = useState(false);
  const suggestedCategories = Array.from(
    new Set(gallery.map((item) => String(item.category || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    const load = async () => {
      const items = await authedJson('/api/admin/gallery').catch(() => []);
      setGallery(Array.isArray(items) ? items : []);
    };
    load();
  }, []);

  const [itemForm, setItemForm] = useState({ id: '', title: '', category: '', image: '', type: 'image', date: '' });
  const [editingIdx, setEditingIdx] = useState(-1);

  const handleGalleryFileChange = async (file) => {
    if (!file) return;
    const mime = String(file.type || '');
    setFeedback({ error: '', success: '' });

    if (mime.startsWith('image/')) {
      try {
        setUploading(true);
        setFeedback({ error: '', success: 'Uploading image...' });
        const imageUrl = await uploadImageToGalleryMedia(file, 'gallery-images');
        setItemForm((prev) => ({ ...prev, image: imageUrl, type: 'image' }));
        setFeedback({ error: '', success: 'Image uploaded successfully.' });
      } catch (error) {
        setFeedback({ error: `Image upload failed: ${error.message}`, success: '' });
      } finally {
        setUploading(false);
      }
      return;
    }

    if (mime.startsWith('video/')) {
      try {
        setUploading(true);
        setFeedback({ error: '', success: 'Uploading video...' });
        const publicUrl = await uploadVideoToGalleryMedia(file, 'gallery-videos');
        if (!publicUrl) throw new Error('Could not get public video URL');
        setItemForm((prev) => ({ ...prev, image: publicUrl, type: 'video' }));
        setFeedback({ error: '', success: 'Video uploaded successfully.' });
      } catch (error) {
        setFeedback({
          error: `Video upload failed: ${error.message}. Ensure bucket "${GALLERY_STORAGE_BUCKET}" exists and is public.`,
          success: ''
        });
      } finally {
        setUploading(false);
      }
      return;
    }

    setFeedback({ error: 'Only image or video files are allowed.', success: '' });
  };

  const addOrUpdateItem = async (e) => {
    e.preventDefault();
    setFeedback({ error: '', success: '' });
    try {
      if (editingIdx >= 0) {
        const current = gallery[editingIdx];
        const updatedItem = await authedJson('/api/admin/gallery', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: current.id,
            title: itemForm.title,
            category: itemForm.category,
            image_url: itemForm.image,
            type: itemForm.type,
            date_label: itemForm.date
          })
        });
        const updated = [...gallery];
        updated[editingIdx] = updatedItem;
        setGallery(updated);
        setEditingIdx(-1);
        setFeedback({ error: '', success: 'Gallery item updated.' });
      } else {
        const created = await authedJson('/api/admin/gallery', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: itemForm.title,
            category: itemForm.category,
            image_url: itemForm.image,
            type: itemForm.type,
            date_label: itemForm.date
          })
        });
        setGallery([created, ...gallery]);
        setFeedback({ error: '', success: 'Gallery item added.' });
      }
    } catch (error) {
      setFeedback({ error: error.message, success: '' });
    }
    setItemForm({ id: '', title: '', category: '', image: '', type: 'image', date: '' });
  };

  const editItem = (idx) => {
    const item = gallery[idx];
    setEditingIdx(idx);
    setItemForm({ id: item.id, title: item.title, category: item.category, image: item.image_url, type: item.type, date: item.date_label });
  };

  const deleteItem = async (idx) => {
    if (!window.confirm('Delete this gallery item?')) return;
    setFeedback({ error: '', success: '' });
    try {
      const id = gallery[idx].id;
      await authedJson('/api/admin/gallery', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      setGallery(gallery.filter((_, i) => i !== idx));
      setFeedback({ error: '', success: 'Gallery item deleted.' });
    } catch (error) {
      setFeedback({ error: error.message, success: '' });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="bg-[#0A0E11] rounded-xl p-8 border border-[#A1FF4F]/10">
      <h2 className="text-2xl font-bold mb-4">Gallery Editor</h2>
      {feedback.error && <p className="mb-3 text-red-300 text-sm">{feedback.error}</p>}
      {feedback.success && <p className="mb-3 text-[#A1FF4F] text-sm">{feedback.success}</p>}
      <form onSubmit={addOrUpdateItem} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <input value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} placeholder="title" className="p-3 bg-[#0F1419] rounded" />
        <input
          value={itemForm.category}
          onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
          placeholder="category"
          list="gallery-category-suggestions"
          className="p-3 bg-[#0F1419] rounded"
        />
        <datalist id="gallery-category-suggestions">
          {suggestedCategories.map((category) => (
            <option key={category} value={category} />
          ))}
        </datalist>
        <input value={itemForm.image} onChange={(e) => setItemForm({ ...itemForm, image: e.target.value })} placeholder="image url (optional)" className="p-3 bg-[#0F1419] rounded" />
        <input
          type="file"
          accept="image/*,video/*"
          onChange={(e) => handleGalleryFileChange(e.target.files?.[0])}
          className="p-3 bg-[#0F1419] rounded text-white/70"
        />
        <input value={itemForm.date} onChange={(e) => setItemForm({ ...itemForm, date: e.target.value })} placeholder="date" className="p-3 bg-[#0F1419] rounded" />
        <select value={itemForm.type} onChange={(e) => setItemForm({ ...itemForm, type: e.target.value })} className="p-3 bg-[#0F1419] rounded text-white/80">
          <option value="image">image</option>
          <option value="video">video</option>
        </select>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={uploading} className="px-4 py-2 bg-[#A1FF4F]/20 text-[#A1FF4F] rounded disabled:opacity-50 disabled:cursor-not-allowed">
            {uploading ? 'Uploading...' : (editingIdx >= 0 ? 'Update' : 'Add')}
          </button>
          {editingIdx >= 0 && <button type="button" onClick={() => { setEditingIdx(-1); setItemForm({ id: '', title: '', category: '', image: '', type: 'image', date: '' }); }} className="px-3 py-2 bg-white/5 rounded">Cancel</button>}
        </div>
      </form>

      <div className="space-y-3">
        {gallery.map((item, idx) => (
          <div key={item.id} className="flex items-center justify-between p-3 bg-white/5 rounded">
            <div className="flex items-center gap-3">
              {item.type === 'video' ? (
                <video
                  src={item.image_url || item.image}
                  className="w-16 h-10 object-cover rounded bg-black"
                  muted
                  playsInline
                />
              ) : (
                <img src={item.image_url || item.image} alt={item.title} className="w-16 h-10 object-cover rounded" />
              )}
              <div>
                <div className="font-semibold">{item.title}</div>
                <div className="text-sm text-white/60">{item.category} • {item.date_label || item.date}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => editItem(idx)} className="p-2 bg-white/5 rounded"><Edit className="w-4 h-4" /></button>
              <button onClick={() => deleteItem(idx)} className="p-2 bg-red-600/20 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {gallery.length === 0 && <p className="text-white/60 mt-4">No gallery items yet.</p>}
    </motion.div>
  );
}
